import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Code2, GraduationCap, UserCircle, Blocks, Home, BookOpen, Download, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase, getSupabaseUrl } from '../supabase/client';
import { getBeijingToday } from '../utils/timeUtils';
import LogoWithFallback from './LogoWithFallback';

interface HeaderProps {
  activePath?: string;
  hideOnPaths?: string[];
}

interface NavItemConfig {
  name: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Home,
  BookOpen,
  Download,
  Code2,
  Blocks,
  GraduationCap,
  User,
  UserCircle,
};

const defaultNavItems: NavItemConfig[] = [
  { name: '首页', path: '/' },
  { name: '刷题平台', path: '/exam-categories' },
  { name: '下载', path: '/downloads' },
];

export default function Header({ activePath, hideOnPaths = [] }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [navItems, setNavItems] = useState<NavItemConfig[]>(defaultNavItems);
  const [hasValidPackage, setHasValidPackage] = useState(false);
  const [avatarSeed, setAvatarSeed] = useState<string>('');
  const [auditStatus, setAuditStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  // 网络重连提示状态：'idle' | 'reconnecting' | 'failed'
  const [reconnectStatus, setReconnectStatus] = useState<'idle' | 'reconnecting' | 'failed'>('idle');
  const userRef = useRef<any>(null);
  userRef.current = user;
  // 重试计数器引用，避免闭包陷阱
  const retryCountRef = useRef(0);
  const isRetryingRef = useRef(false);

  const currentPath = activePath || location.pathname;
  const shouldHide = hideOnPaths.some(path => {
    if (path.endsWith('/*')) {
      const basePath = path.slice(0, -2);
      return currentPath.startsWith(basePath);
    }
    return currentPath === path || currentPath.startsWith(path + '/');
  });

  if (shouldHide) return null;

  // 获取头像URL - 与 AssignmentsPage 同步，教师使用固定剪影头像
  const getAvatarUrl = () => {
    // 教师使用固定的简洁中性头像（notionists风格）
    if (user?.role === 'teacher') {
      return 'https://api.dicebear.com/7.x/notionists/svg?seed=teacher&backgroundColor=f1f5f9';
    }
    // 学生使用自定义头像
    if (avatarSeed) {
      const parts = avatarSeed.split(':');
      if (parts.length === 2) {
        const [style, seed] = parts;
        const bgColors: Record<string, string> = {
          bottts: 'e0e7ff',
          avataaars: 'ffe4e6',
          'fun-emoji': 'fef3c7',
          adventurer: 'dbeafe',
          micah: 'd1fae5',
          'big-smile': 'fce7f3',
          personas: 'e0e7ff',
          lorelei: 'ede9fe',
          notionists: 'f0fdf4',
          'open-peeps': 'fff7ed',
          'pixel-art': 'ecfccb',
          shapes: 'cffafe',
        };
        const bgColor = bgColors[style] || 'e0e7ff';
        return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&backgroundColor=${bgColor}`;
      }
    }
    return `https://api.dicebear.com/7.x/bottts/svg?seed=${user?.id || user?.phone || 'user'}&backgroundColor=f1f5f9`;
  };

  useEffect(() => {
    const loadNavItems = async () => {
      try {
        const { data, error } = await supabase
          .from('nav_items')
          .select('*')
          .eq('is_visible', true)
          .order('sort_order', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          const mappedItems: NavItemConfig[] = data
            .filter(item => item.path !== '/assignments')
            .map(item => ({
              name: item.name,
              path: item.path,
              icon: item.icon ? iconMap[item.icon] : undefined,
            }));
          setNavItems(mappedItems);
        }
      } catch (err) {
        console.error('加载导航项失败:', err);
      }
    };

    loadNavItems();

    const loadUserRole = async (userId: string) => {
      // 获取用户头像种子和审核状态
      const { data: profileData } = await supabase
        .from('profiles')
        .select('avatar_seed, phone, role, username, teacher_nickname, audit_status')
        .eq('id', userId)
        .maybeSingle();

      if (profileData?.avatar_seed) {
        setAvatarSeed(profileData.avatar_seed);
      }

      // 设置审核状态
      if (profileData?.audit_status) {
        setAuditStatus(profileData.audit_status);
      } else {
        setAuditStatus(null);
      }

      const { data: accountData } = await supabase
        .from('user_accounts')
        .select('role, mobile, username')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (accountData) {
        setUser({ id: userId, phone: accountData.mobile || '', role: accountData.role || 'student', username: accountData.username || '', teacher_nickname: profileData?.teacher_nickname || '' });
      } else if (profileData) {
        setUser({ id: userId, phone: profileData.phone || '', role: profileData.role || 'student', username: profileData.username || '', teacher_nickname: profileData.teacher_nickname || '' });
      }
    };

    const loadUnreadCount = async (userId: string) => {
      try {
        const session = (await supabase.auth.getSession()).data.session;
        if (!session) return;
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: 'messages_get_unread_count', userId }),
        });
        const result = await response.json();
        if (result.success) {
          setUnreadCount(result.count);
        }
      } catch (error) {

      }
    };

    const checkUserPackage = async (userId: string) => {
      try {
        const now = getBeijingToday();
        const { data, error } = await supabase
          .from('user_packages')
          .select('id')
          .eq('user_id', userId)
          .eq('is_active', true)
          .gte('expire_date', now)
          .limit(1);

        if (error) {
          console.error('检查用户套餐失败:', error);
          setHasValidPackage(false);
          return;
        }

        setHasValidPackage(data && data.length > 0);
      } catch (error) {
        console.error('检查用户套餐出错:', error);
        setHasValidPackage(false);
      }
    };

    const handleMessageStatusChange = () => {
      const userId = userRef.current?.id;
      if (userId) {
        loadUnreadCount(userId);
      }
    };
    window.addEventListener('message-status-change', handleMessageStatusChange);

    const handleUserInfoUpdate = () => {
      const userId = userRef.current?.id;
      if (userId) {
        loadUserRole(userId);
        checkUserPackage(userId);
      }
    };
    window.addEventListener('user-info-updated', handleUserInfoUpdate);

    // 监听套餐购买成功事件
    const handlePackagePurchased = () => {
      const userId = userRef.current?.id;
      if (userId) {
        checkUserPackage(userId);
      }
    };
    window.addEventListener('package-purchased', handlePackagePurchased);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // 区分事件类型：
      // - SIGNED_OUT: 用户主动登出，立即清空状态
      // - TOKEN_REFRESH_FAILURE: Token 刷新失败，进入重试流程，不立即清空
      // - 其他事件（SIGNED_IN / TOKEN_REFRESHED / INITIAL_SESSION）：按 session 是否存在处理
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUnreadCount(0);
        setHasValidPackage(false);
        setReconnectStatus('idle');
        return;
      }

      if (event === 'TOKEN_REFRESH_FAILURE') {
        // Token 刷新失败，不立即清空用户状态，进入重试流程
        console.warn('[Auth] Token 刷新失败，启动重试流程');
        if (!isRetryingRef.current) {
          retryCountRef.current = 0;
          attemptReconnect();
        }
        return;
      }

      // 正常事件：有 session 则恢复用户，无 session 则清空
      if (session?.user) {
        // 重连成功，重置状态
        if (isRetryingRef.current) {
          isRetryingRef.current = false;
          retryCountRef.current = 0;
          setReconnectStatus('idle');
        }
        setTimeout(() => {
          loadUserRole(session.user.id);
          loadUnreadCount(session.user.id);
          checkUserPackage(session.user.id);
        }, 0);
      } else {
        setUser(null);
        setUnreadCount(0);
        setHasValidPackage(false);
      }
    });

    // Token 刷新失败后的重连逻辑：最多重试 3 次，间隔 2 秒
    const attemptReconnect = async () => {
      if (isRetryingRef.current) return;
      isRetryingRef.current = true;
      setReconnectStatus('reconnecting');

      for (let i = 1; i <= 3; i++) {
        retryCountRef.current = i;
        console.log(`[Auth] 重连尝试 ${i}/3`);
        // 等待 2 秒
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) {
            console.warn(`[Auth] 重连 ${i} 失败:`, error.message);
            continue;
          }
          if (session?.user) {
            // 重连成功
            console.log(`[Auth] 重连 ${i} 成功，恢复用户状态`);
            isRetryingRef.current = false;
            retryCountRef.current = 0;
            setReconnectStatus('idle');
            // 触发用户状态恢复
            loadUserRole(session.user.id);
            loadUnreadCount(session.user.id);
            checkUserPackage(session.user.id);
            return;
          }
          console.warn(`[Auth] 重连 ${i}: session 仍为空`);
        } catch (err) {
          console.warn(`[Auth] 重连 ${i} 异常:`, err);
        }
      }

      // 3 次重试全部失败，清空用户并跳转登录页
      console.error('[Auth] 重连全部失败，跳转登录页');
      isRetryingRef.current = false;
      retryCountRef.current = 0;
      setReconnectStatus('failed');
      setUser(null);
      setUnreadCount(0);
      setHasValidPackage(false);
      // 延迟 1.5 秒后跳转，让用户看到失败提示
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    };

    // 监听全局 401 事件（由 authFetch 拦截器触发）
    const handleSessionExpired = () => {
      console.warn('[Auth] 收到全局会话过期事件');
      if (!isRetryingRef.current && userRef.current) {
        retryCountRef.current = 0;
        attemptReconnect();
      }
    };
    window.addEventListener('auth-session-expired', handleSessionExpired);

    // 说明：不再单独调用 supabase.auth.getSession() 做启动期恢复。
    // onAuthStateChange 订阅后 gotrue-js 会发出 INITIAL_SESSION 事件（携带当前 session），
    // 上面的回调已处理该事件；额外的 getSession() 会在启动期与订阅争用同一把
    // navigator lock（sb-*-auth-token），在 React StrictMode 双挂载下触发
    // "Lock was not released within 5000ms" 警告与 AbortError。

    const interval = setInterval(() => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          loadUnreadCount(session.user.id);
        }
      }).catch((err) => {
        // 锁被 steal 打断等异常不应变成 unhandledrejection
        console.warn('[Auth] 轮询会话失败:', err?.message || err);
      });
    }, 60000);

    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('message-status-change', handleMessageStatusChange);
      window.removeEventListener('user-info-updated', handleUserInfoUpdate);
      window.removeEventListener('package-purchased', handlePackagePurchased);
      window.removeEventListener('auth-session-expired', handleSessionExpired);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/38 backdrop-blur-xl shadow-lg shadow-blue-100/30 border-b border-blue-100/30`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <LogoWithFallback className="h-10 w-10 object-contain flex-shrink-0" />
      <div className="flex items-baseline gap-0 select-none leading-none" style={{ fontFamily: "'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Noto Sans SC', sans-serif" }}>
         <span className="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">编程优题库</span>
                <span className="text-lg sm:text-xl font-bold ml-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-200">UDO100.com</span>
              </div>
            </Link>

            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative px-4 py-2 text-sm font-medium transition-all rounded-lg ${
                      isActive(item.path)
                        ? 'text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {isActive(item.path) && (
                      <motion.div
                        layoutId="navHighlight"
                        className="absolute inset-0 bg-blue-50 rounded-lg"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              {/* 教师中心/我的作业按钮 */}
              {(() => {
                const isTeacherPending = user?.role === 'teacher' && auditStatus !== 'approved';
                const teacherCenterPath = user ? (user.role === 'teacher' ? '/teacher/assignments' : '/assignments') : '/login';

                return (
                  <Link
                    to={teacherCenterPath}
                    state={user ? undefined : { from: '/assignments' }}
                    onClick={isTeacherPending ? (e) => { e.preventDefault(); alert('您的教师认证正在审核中，审核通过后方可访问'); } : undefined}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all shadow-sm ${
                      isTeacherPending
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : location.pathname === '/assignments' || location.pathname.startsWith('/assignments/') || location.pathname.startsWith('/teacher/assignments')
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-blue-500/30'
                        : 'bg-white/80 text-blue-600 border border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                    }`}
                  >
                    <ClipboardList className="w-4 h-4" />
                    <span>{user ? (user.role === 'teacher' ? '教师中心' : '我的作业') : '我的作业'}</span>
                  </Link>
                );
              })()}

              {user ? (
                <div className="flex items-center gap-2">
                  <div className="w-px h-5 bg-gray-200 mx-1" />

                  <button
                    onClick={() => navigate(user?.role === 'teacher' ? '/teacher/basic-info' : '/profile')}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:bg-gray-100 group relative"
                  >
                    <div className="relative">
                      <img
                        src={getAvatarUrl()}
                        alt="用户头像"
                        className={`w-6 h-6 rounded-full border transition-colors ${
                          hasValidPackage
                            ? 'border-yellow-400 group-hover:border-yellow-500'
                            : 'border-slate-200 group-hover:border-blue-300'
                        }`}
                      />
                    </div>
                    <span
                      className={`text-sm font-medium underline underline-offset-2 cursor-pointer transition-colors ${
                        hasValidPackage
                          ? 'text-amber-500 decoration-amber-300 hover:text-amber-600 hover:decoration-amber-500'
                          : 'text-blue-600 decoration-blue-300 hover:text-blue-700 hover:decoration-blue-500'
                      }`}
                    >
                      {user.role === 'teacher' ? (user.teacher_nickname || user.username || (user.phone?.includes('@') ? user.phone.split('@')[0] : user.phone)) : (user.username || (user.phone?.includes('@') ? user.phone.split('@')[0] : user.phone))}
                    </span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -left-0.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex items-center">
                  <Link
                    to="/login"
                    className={`px-2 py-2 text-sm rounded-lg transition-all ${
                      location.pathname === '/login'
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-600/25'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    登录
                  </Link>
                  <Link
                    to="/register"
                    className={`px-2 py-2 text-sm rounded-lg transition-all ${
                      location.pathname === '/register'
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-600/25'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    注册
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="h-16" />

      {/* 网络重连提示条 */}
      {reconnectStatus !== 'idle' && (
        <div className={`fixed top-16 left-0 right-0 z-[55] px-4 py-2 text-center text-sm font-medium shadow-md ${
          reconnectStatus === 'reconnecting'
            ? 'bg-amber-50 text-amber-700 border-b border-amber-200'
            : 'bg-red-50 text-red-700 border-b border-red-200'
        }`}>
          {reconnectStatus === 'reconnecting' ? (
            <span className="inline-flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              网络异常，正在重连...（第 {retryCountRef.current}/3 次）
            </span>
          ) : (
            <span>连接已断开，正在跳转登录页...</span>
          )}
        </div>
      )}
    </>
  );
}
